import { createMsalInstance, getMsalInstance } from "./initializeMsalLibrary";
import { getAccountFromMsal, getActiveAccountFromMsal } from "./utils/MsalAccounts";
import { trace } from "owa-trace";
import getModuleContextMailboxInfo from "owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo";
import { removeIsAuthenticated } from "./isAnonymousSession";
import { setOwaAppId } from "owa-config/lib/getOwaAppId";
import type { AccountInfo } from "@azure/msal-browser-1p";
import { getPostLogoutRedirectUri } from "./utils/getPostLogoutRedirectUri";
import { clearCookies } from "./utils/clearCookies";
import isAccountSourceListStoreInitialized from "owa-account-source-list-store/lib/utils/isAccountSourceListStoreInitialized";
import { removeItem } from "owa-local-storage";
import { getAuthConfig } from "owa-auth-callbacks";
import { logStartCoreUsage } from "owa-analytics-start";
import { getLogAuthErrorData } from "./utils/logger";
import { InteractionType } from "./utils/InteractionType";
import { getTypeHintFromAccountInfo } from "./getTypeHint";
import { toAuthError } from "./utils/toAuthError";

export default async function signoutFromMsal(isSharedABT?: boolean): Promise<void> {
    const startTime = self.performance.now();
    let userAccount: AccountInfo | null = null;
    clearLocalStorage();
    
    try {
        try {
            // signout should work even if MSAL hasn't been used during the session
            createMsalInstance();
        }
        catch {
            // noop - error expected here if MSAL already initialized
        }
        
        const msalInstance = await getMsalInstance();
        
        if (isAccountSourceListStoreInitialized()) {
            userAccount = getAccountFromMsal(msalInstance, getModuleContextMailboxInfo());
        }
        else {
            userAccount = getActiveAccountFromMsal(msalInstance);
        }
        
        if (userAccount != null) {
            setOwaAppId(null);
            removeIsAuthenticated();
            await clearCookies(userAccount.tenantId);
            await msalInstance.logoutRedirect({
                account: userAccount,
                postLogoutRedirectUri: getPostLogoutRedirectUri(userAccount, isSharedABT),
            });
        }
    }
    catch (e) {
        trace.info("MsalInitFailure-LogoutRedirect " + { Error: e });
        logStartCoreUsage("MsalSignoutFailure", getModuleContextMailboxInfo(), {
            ...getLogAuthErrorData(InteractionType.Redirect, toAuthError(e), startTime, "SignoutFromMsal", undefined /* scopes */, userAccount != null
                ? getTypeHintFromAccountInfo(userAccount)
                : undefined /* typeHint */, undefined /* isUrlPresent */, undefined /* isCAERemediationRequired */, self.document.visibilityState === "visible" /* isPageVisible */, undefined /* isSigninRequired */, userAccount != null /* isAccountPresent */),
            isSharedABT: !!isSharedABT,
        });
    }
}

function clearLocalStorage(): void {
    removeItem(self, "login_hint"); // clear login_hint from local storage upon logout
    removeItem(self, "domain_hint"); // clear domain_hint from local storage upon logout
    getAuthConfig().deleteLoginHintClaim?.(getModuleContextMailboxInfo());
}
